import { useMemo, useEffect, useRef, useCallback } from 'react';
import { useFocusState } from './useFocusState';
import { useFocusCalculations } from './useFocusCalculations';
import { useFocusViewReset } from './useFocusViewReset';
import { filterEdgesForDisplay } from '../utils/focusModeUtils';

/**
 * useFocusMode Hook
 * 
 * Main entry point for focus mode functionality.
 * Composes smaller hooks for state, calculations, and view management.
 * 
 * FOKUS MODE LOGIC:
 * Shows selected tasks with ALL incomplete upstream dependencies
 * (recursively until completed) and +1 downstream (immediate children).
 * Answers: "What do I need to complete to work on X?"
 */
export function useFocusMode({
  tasks,
  nodePositions,
  nodeDimensions,
  edges,
  containerRef,
  pan,
  zoom,
  setPan,
  setZoom,
  onFocusModeChange,
  isMultiCampaignMode = false,
  statuses = [],
  initialFocusedTaskIds = null, // For restoring FOKUS state after tab switch
}) {
  // Track if we're doing an instant transition (skip animated view reset)
  // This is set by handleFocusTasksInstant and cleared after the effect runs
  const skipAnimatedViewResetRef = useRef(false);
  
  // State management
  const focusState = useFocusState({
    tasks,
    onFocusModeChange,
    initialFocusedTaskIds,
  });
  
  const {
    focusedTaskIds,
    lastFocusedTaskIds,
    isFocusMode,
    handleFocusTask,
    handleFocusTasks,
    handleExitFocusMode,
    addToFocusedTasks,
    // Instant handlers (no animation - for view switching)
    handleFocusTasksInstant: focusStateHandleFocusTasksInstant,
    handleExitFocusModeInstant: focusStateHandleExitFocusModeInstant,
  } = focusState;
  
  // Wrap instant handlers to set the skip flag
  const handleFocusTasksInstant = useCallback((taskIds) => {
    skipAnimatedViewResetRef.current = true;
    focusStateHandleFocusTasksInstant(taskIds);
  }, [focusStateHandleFocusTasksInstant]);
  
  const handleExitFocusModeInstant = useCallback(() => {
    skipAnimatedViewResetRef.current = true;
    focusStateHandleExitFocusModeInstant();
  }, [focusStateHandleExitFocusModeInstant]);
  
  // Calculations
  const calculations = useFocusCalculations({
    tasks,
    focusedTaskIds,
    nodePositions,
    nodeDimensions,
    containerRef,
    pan,
    zoom,
    isMultiCampaignMode,
    statuses,
  });
  
  const {
    decisionAnswers,
    allFocusedTaskIds,
    hiddenConnections,
    primaryFocusedIds,
    displayTasks,
    displayPositions,
    focusAutoPositions,
    focusContentBounds,
    lastFocusPositions,
    lastFocusEdges,
    lastFocusTasks,
  } = calculations;
  
  // View reset and animation
  const viewReset = useFocusViewReset({
    containerRef,
    nodePositions,
    nodeDimensions,
    allFocusedTaskIds,
    focusAutoPositions,
    pan,
    zoom,
    setPan,
    setZoom,
  });
  
  const {
    centerOnTask,
    panToPosition,
    resetViewForFocusMode,
    resetViewForFokusModeInstant,
    panBackToNodes,
    panToFocusLayout,
    zoomToFocusLayout,
    zoomOutForExit,
  } = viewReset;
  
  // Generate display edges with the actual edges from props
  const displayEdges = useMemo(() => {
    const focusEdges = calculations.displayEdges;
    return filterEdgesForDisplay({
      focusEdges,
      edges,
      allFocusedTaskIds,
      decisionAnswers,
      hasFocusEdges: !!(focusedTaskIds && focusEdges),
    });
  }, [calculations.displayEdges, edges, allFocusedTaskIds, decisionAnswers, focusedTaskIds]);
  
  // Track displayed tasks changes for view reset during TRANSITIONS
  // (when focus targets change while already in focus mode)
  // Initial entry animation is handled by useFocusAnimation
  const displayedTasksSignature = useMemo(() => {
    if (!allFocusedTaskIds) return null;
    return Array.from(allFocusedTaskIds).sort().join(',');
  }, [allFocusedTaskIds]);
  
  const prevDisplayedTasksRef = useRef(null);
  const prevFocusedIdsRef = useRef(null);
  const wasInFocusModeRef = useRef(false);
  // Track when we should skip animated resets (set by instant transition, cleared after cooldown)
  const skipAnimatedResetUntilRef = useRef(0);
  
  useEffect(() => {
    // Skip animated view reset if we're doing an instant transition (view switching)
    // Use a timestamp-based cooldown to handle multiple effect runs
    const now = Date.now();
    if (skipAnimatedViewResetRef.current) {
      // Set a cooldown period (500ms) during which we won't do animated resets
      skipAnimatedResetUntilRef.current = now + 500;
      skipAnimatedViewResetRef.current = false;
    }
    
    const isInCooldown = now < skipAnimatedResetUntilRef.current;
    
    if (isInCooldown) {
      // Still update tracking refs, but skip the animated reset
      if (displayedTasksSignature) {
        prevDisplayedTasksRef.current = displayedTasksSignature;
      }
      if (focusedTaskIds) {
        prevFocusedIdsRef.current = new Set(focusedTaskIds);
        wasInFocusModeRef.current = true;
      } else {
        wasInFocusModeRef.current = false;
      }
      return;
    }
    
    // Only handle transitions when ALREADY in focus mode
    // Initial entry is handled by useFocusAnimation's onStartPanToFocusLayout
    const isAlreadyInFocusMode = wasInFocusModeRef.current;
    
    if (focusedTaskIds && 
        displayedTasksSignature && 
        prevDisplayedTasksRef.current && 
        displayedTasksSignature !== prevDisplayedTasksRef.current &&
        isAlreadyInFocusMode) {
      
      // Check if this is an expansion (all previous IDs still present) vs a transition
      // If expanding, don't reset the view - just let layout update naturally
      const prevIds = prevFocusedIdsRef.current;
      const isExpansion = prevIds && Array.from(prevIds).every(id => focusedTaskIds.has(id));
      
      if (!isExpansion) {
        // This is a real transition (different focus targets), reset view
        resetViewForFocusMode();
      }
      // else: Just expanding focus, don't pan/zoom - layout will update naturally
    }
    
    // Update tracking refs
    if (displayedTasksSignature) {
      prevDisplayedTasksRef.current = displayedTasksSignature;
    }
    if (focusedTaskIds) {
      prevFocusedIdsRef.current = new Set(focusedTaskIds);
      wasInFocusModeRef.current = true;
    } else {
      wasInFocusModeRef.current = false;
    }
  }, [displayedTasksSignature, focusedTaskIds, resetViewForFocusMode]);
  
  // Get focused task names for display
  const focusedTaskNames = useMemo(() => {
    if (!primaryFocusedIds) return [];
    return Array.from(primaryFocusedIds)
      .map(id => tasks.find(t => t.id === id)?.name || 'Mission')
      .filter(Boolean);
  }, [primaryFocusedIds, tasks]);
  
  return {
    // State
    focusedTaskIds: primaryFocusedIds,
    allFocusedTaskIds,
    lastFocusedTaskIds,
    focusedTaskNames,
    
    // Calculated values
    hiddenConnections,
    displayTasks,
    displayPositions,
    displayEdges,
    focusContentBounds,
    
    // Last focus layout (for exit animation)
    lastFocusPositions,
    lastFocusEdges,
    lastFocusTasks,
    
    // Handlers
    handleFocusTask,
    handleFocusTasks,
    handleExitFocusMode,
    addToFocusedTasks,
    
    // Instant handlers (no animation - for view switching)
    handleFocusTasksInstant,
    handleExitFocusModeInstant,
    
    // Pan helpers for animation
    centerOnTask,
    panToPosition,
    resetViewForFocusMode,
    resetViewForFokusModeInstant,
    panBackToNodes,
    // New split functions for sequenced animations
    panToFocusLayout,
    zoomToFocusLayout,
    zoomOutForExit,
    
    // For imperative handle
    isFocusMode,
  };
}

export { useFocusState } from './useFocusState';
export { useFocusCalculations } from './useFocusCalculations';
export { useFocusViewReset } from './useFocusViewReset';

